// @ts-nocheck
/**
 * AI Tutoring Chat Interface
 * Real-time WebSocket-based conversational tutoring with Socratic method
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Tile,
  TextInput,
  TextArea,
  Button,
  Stack,
  Tag,
  Loading,
  InlineNotification,
  Select,
  Grid,
  Column,
  ProgressIndicator,
  UnorderedList,
} from '@carbon/react';
import { Bot, User, Security, Education } from '@carbon/icons-react';
import { authService } from '../../services/authService';

// Typography components handled differently in Carbon

interface Message {
  id: string;
  role: 'student' | 'ai_tutor' | 'system';
  content: string;
  timestamp: string;
  metadata?: {
    socraticMethodUsed?: boolean;
    conceptTags?: string[];
    followUpQuestions?: string[];
    processingTime?: number;
    safetyScore?: number;
    educationalIntent?: string;
  };
}

interface SessionContext {
  subject: string;
  topic?: string;
  difficultyLevel: number;
  learningObjectives: string[];
}

interface ConnectionStatus {
  isConnected: boolean;
  isReconnecting: boolean;
  lastError?: string;
  sessionId?: string;
}

interface SafetyMetrics {
  contentSafetyScore: number;
  educationalValueScore: number;
  socraticMethodCompliance: number;
}

const AiTutoringChat: React.FC = () => {
  // State management
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    isConnected: false,
    isReconnecting: false,
  });
  const [sessionContext, setSessionContext] = useState<SessionContext>({
    subject: 'mathematics',
    difficultyLevel: 5,
    learningObjectives: ['Develop critical thinking', 'Build problem-solving skills'],
  });
  const [safetyMetrics, setSafetyMetrics] = useState<SafetyMetrics>({
    contentSafetyScore: 1.0,
    educationalValueScore: 1.0,
    socraticMethodCompliance: 1.0,
  });
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [conceptsCovered, setConceptsCovered] = useState<string[]>([]);

  // WebSocket reference
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Session duration timer
  useEffect(() => {
    if (sessionStartTime && connectionStatus.isConnected) {
      const interval = setInterval(() => {
        const duration = Math.floor((Date.now() - sessionStartTime.getTime()) / 1000);
        setSessionDuration(duration);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [sessionStartTime, connectionStatus.isConnected]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(async () => {
    try {
      // Get authentication token
      const token = authService.getToken();
      if (!token) {
        // notification.error('Please log in to start tutoring');
        return;
      }

      // Start tutoring session via API
      const response = await fetch('/api/tutoring/session/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          student_id: authService.getCurrentUser()?.userId || 'STU-TEST-001',
          subject_area: sessionContext.subject,
          session_context: {
            curriculum_topic: sessionContext.topic,
            difficulty_preference: getDifficultyPreference(sessionContext.difficultyLevel),
            learning_objective: sessionContext.learningObjectives[0],
          },
          parent_supervision_level: 'monitoring',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to start tutoring session');
      }

      const sessionData = await response.json();
      const { session_id, websocket_url, session_token } = sessionData.data;

      // Connect to WebSocket
      const wsUrl = `${websocket_url}&token=${session_token}`;
      const ws = new WebSocket(wsUrl.replace('http', 'ws'));

      ws.onopen = () => {
        setConnectionStatus({
          isConnected: true,
          isReconnecting: false,
          sessionId: session_id,
        });
        setSessionStartTime(new Date());

        // Send initial context
        ws.send(
          JSON.stringify({
            type: 'update_context',
            subject: sessionContext.subject,
            topic: sessionContext.topic,
            difficultyLevel: sessionContext.difficultyLevel,
            learningObjectives: sessionContext.learningObjectives,
          })
        );

        // Start heartbeat
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'heartbeat' }));
          }
        }, 30000);

        // Add welcome message
        addSystemMessage(
          "Welcome to your AI tutoring session! I'm Sigma, and I'm here to help you learn through discovery. What would you like to explore today?"
        );
      };

      ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      ws.onerror = error => {
        console.error('WebSocket error:', error);
        setConnectionStatus(prev => ({
          ...prev,
          lastError: 'Connection error occurred',
        }));
      };

      ws.onclose = event => {
        setConnectionStatus({
          isConnected: false,
          isReconnecting: true,
          lastError: event.reason || 'Connection closed',
        });

        // Clear heartbeat
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
        }

        // Attempt reconnection if not intentionally closed
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 5000);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to connect:', error);
      // notification.error('Failed to start tutoring session');
      setConnectionStatus({
        isConnected: false,
        isReconnecting: false,
        lastError: 'Failed to connect',
      });
    }
  }, [sessionContext]);

  // Handle incoming WebSocket messages
  const handleWebSocketMessage = (data: any) => {
    switch (data.type) {
      case 'ai_response':
        setIsTyping(false);
        addMessage({
          id: data.messageId,
          role: 'ai_tutor',
          content: data.content,
          timestamp: data.timestamp,
          metadata: data.metadata,
        });

        // Update concepts covered
        if (data.metadata?.conceptTags) {
          setConceptsCovered(prev => [...new Set([...prev, ...data.metadata.conceptTags])]);
        }

        // Update safety metrics
        if (data.metadata?.safetyScore) {
          setSafetyMetrics(prev => ({
            ...prev,
            educationalValueScore: data.metadata.safetyScore,
          }));
        }

        if (data.metadata?.socraticMethodUsed) {
          setSafetyMetrics(prev => {
            const messageCount = messages.filter(m => m.role === 'ai_tutor').length + 1;
            const socraticCount =
              messages.filter(m => m.role === 'ai_tutor' && m.metadata?.socraticMethodUsed).length +
              (data.metadata.socraticMethodUsed ? 1 : 0);
            return {
              ...prev,
              socraticMethodCompliance: socraticCount / messageCount,
            };
          });
        }
        break;

      case 'ai_thinking':
        setIsTyping(true);
        break;

      case 'session_ended':
        handleSessionEnd(data.summary);
        break;

      case 'provider_switched':
        addSystemMessage(`AI provider switched to ${data.newProvider} for better response quality`);
        break;

      case 'error':
        // notification.error(data.message);
        setIsTyping(false);
        break;

      case 'heartbeat_ack':
        // Connection is alive
        break;

      default:
    }
  };

  // Send message to AI tutor
  const sendMessage = () => {
    if (!inputMessage.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    const userMessage: Message = {
      id: `msg-${Date.now()}`,
      role: 'student',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString(),
    };

    // Add message to chat
    addMessage(userMessage);

    // Send to WebSocket
    wsRef.current.send(
      JSON.stringify({
        type: 'student_message',
        content: inputMessage.trim(),
        intent: detectEducationalIntent(inputMessage),
      })
    );

    // Clear input and show typing indicator
    setInputMessage('');
    setIsTyping(true);
  };

  // Add message to chat
  const addMessage = (message: Message) => {
    setMessages(prev => [...prev]);
  };

  // Add system message
  const addSystemMessage = (content: string) => {
    addMessage({
      id: `sys-${Date.now()}`,
      role: 'system',
      content,
      timestamp: new Date().toISOString(),
    });
  };

  // Detect educational intent from message
  const detectEducationalIntent = (message: string): string => {
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('how') || lowerMessage.includes('why')) {
      return 'conceptual_understanding';
    } else if (lowerMessage.includes('solve') || lowerMessage.includes('calculate')) {
      return 'problem_solving';
    } else if (lowerMessage.includes('example') || lowerMessage.includes('show')) {
      return 'example_request';
    } else if (lowerMessage.includes('help') || lowerMessage.includes('stuck')) {
      return 'assistance_request';
    } else {
      return 'general_inquiry';
    }
  };

  // Get difficulty preference from level
  const getDifficultyPreference = (level: number): string => {
    if (level <= 3) return 'supportive';
    if (level <= 7) return 'adaptive';
    return 'challenging';
  };

  // Format duration
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle session end
  const handleSessionEnd = (summary: any) => {
    setConnectionStatus({
      isConnected: false,
      isReconnecting: false,
    });

    // notification.success('Tutoring session completed successfully!');

    // Show session summary
    addSystemMessage(`
Session Summary:
- Duration: ${summary.duration} minutes
- Messages exchanged: ${summary.messageCount}
- Socratic compliance: ${(summary.socraticCompliance * 100).toFixed(1)}%
- Concepts covered: ${summary.conceptsCovered.join(', ')}
    `);
  };

  // End session manually
  const endSession = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'end_session',
        })
      );
    }
  };

  // Render message with appropriate styling
  const renderMessage = (msg: Message) => {
    const isAI = msg.role === 'ai_tutor';
    const isSystem = msg.role === 'system';

    return (
      <div
        key={msg.id}
        style={{
          display: 'flex',
          justifyContent: isAI || isSystem ? 'flex-start' : 'flex-end',
          marginBottom: 16,
        }}
      >
        <Stack align="start" style={{ maxWidth: '70%' }}>
          {(isAI || isSystem) && (
            <div
              style={{
                backgroundColor: isAI ? '#1890ff' : '#52c41a',
                borderRadius: '50%',
                padding: 8,
              }}
            >
              {/* Avatar placeholder */}
              <Security />
            </div>
          )}

          <Tile
            size="small"
            style={{
              backgroundColor: isAI || isSystem ? '#f0f2f5' : '#e6f7ff',
              border: 'none',
            }}
          >
            <Stack direction="vertical" size="small">
              <span>{msg.content}</span>

              {msg.metadata?.followUpQuestions && msg.metadata.followUpQuestions.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <span style={{ color: '#525252' }}>
                    <BulbOutlined /> Try asking:
                  </span>
                  {msg.metadata.followUpQuestions.map((q, idx) => (
                    <Tag
                      key={idx}
                      color="blue"
                      style={{ cursor: 'pointer', marginTop: 4 }}
                      onClick={() => setInputMessage(q)}
                    >
                      {q}
                    </Tag>
                  ))}
                </div>
              )}

              {msg.metadata?.conceptTags && msg.metadata.conceptTags.length > 0 && (
                <Stack size="small" wrap>
                  {msg.metadata.conceptTags.map((tag, idx) => (
                    <Tag key={idx} color="green" icon={<Education />}>
                      {tag}
                    </Tag>
                  ))}
                </Stack>
              )}

              <Stack size="small">
                <span style={{ color: '#525252' }}>{new Date(msg.timestamp).toLocaleTimeString()}</span>

                {msg.metadata?.socraticMethodUsed && (
                  <Tooltip title="Socratic method used">
                    <QuestionCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                  </Tooltip>
                )}

                {msg.metadata?.processingTime && (
                  <span style={{ color: '#525252' }}>({(msg.metadata.processingTime / 1000).toFixed(1)}s)</span>
                )}
              </Stack>
            </Stack>
          </Tile>

          {!isAI && !isSystem && (
            <div style={{ backgroundColor: '#87d068', borderRadius: '50%', padding: 8 }}>
              {/* Avatar placeholder */}
              <User />
            </div>
          )}
        </Stack>
      </div>
    );
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Tile size="small" style={{ borderRadius: 0, borderBottom: '1px solid #f0f0f0' }}>
        <Grid align="middle" justify="space-between">
          <Column>
            <Stack>
              <div style={{ backgroundColor: '#1890ff', borderRadius: '50%', padding: 8 }}>
                {/* Avatar placeholder */}
                <Education />
              </div>
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  AI Tutoring Session
                </Title>
                <Stack size="small">
                  <Tag
                    status={connectionStatus.isConnected ? 'success' : 'error'}
                    text={connectionStatus.isConnected ? 'Connected' : 'Disconnected'}
                  />
                  {sessionDuration > 0 && (
                    <span style={{ color: '#525252' }}>
                      <ClockCircleOutlined /> {formatDuration(sessionDuration)}
                    </span>
                  )}
                </Stack>
              </div>
            </Stack>
          </Column>

          <Column>
            <Stack>
              <Select
                value={sessionContext.subject}
                onChange={value => setSessionContext(prev => ({ ...prev, subject: value }))}
                style={{ width: 150 }}
                disabled={connectionStatus.isConnected}
              >
                <SelectItem value="mathematics" text="Mathematics" />
                <SelectItem value="science" text="Science" />
                <SelectItem value="language_arts" text="Language Arts" />
                <SelectItem value="social_studies" text="Social Studies" />
              </Select>

              {!connectionStatus.isConnected ? (
                <Button
                  kind="primary"
                  icon={<SendOutlined />}
                  onClick={connectWebSocket}
                  loading={connectionStatus.isReconnecting}
                >
                  Start Session
                </Button>
              ) : (
                <Button danger icon={<StopOutlined />} onClick={endSession}>
                  End Session
                </Button>
              )}
            </Stack>
          </Column>
        </Grid>
      </Tile>

      {/* Safety Metrics Bar */}
      {connectionStatus.isConnected && (
        <Tile size="small" style={{ margin: '8px 16px' }}>
          <Grid gutter={16}>
            <Column span={8}>
              <span style={{ color: '#525252' }}>Educational Value</span>
              <ProgressIndicator
                percent={Math.round(safetyMetrics.educationalValueScore * 100)}
                strokeColor="#52c41a"
                size="small"
                format={percent => `${percent}%`}
              />
            </Column>
            <Column span={8}>
              <span style={{ color: '#525252' }}>Content Safety</span>
              <ProgressIndicator
                percent={Math.round(safetyMetrics.contentSafetyScore * 100)}
                strokeColor="#1890ff"
                size="small"
                format={percent => `${percent}%`}
              />
            </Column>
            <Column span={8}>
              <span style={{ color: '#525252' }}>Socratic Method</span>
              <ProgressIndicator
                percent={Math.round(safetyMetrics.socraticMethodCompliance * 100)}
                strokeColor="#722ed1"
                size="small"
                format={percent => `${percent}%`}
              />
            </Column>
          </Grid>

          {conceptsCovered.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <span style={{ color: '#525252' }}>Concepts Covered: </span>
              {conceptsCovered.map((concept, idx) => (
                <Tag key={idx} color="cyan" size="small">
                  {concept}
                </Tag>
              ))}
            </div>
          )}
        </Tile>
      )}

      {/* Chat Messages */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 16,
          backgroundColor: '#fafafa',
        }}
      >
        {messages.length === 0 && !connectionStatus.isConnected && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Stack direction="vertical">
                <span>Start a tutoring session to begin learning!</span>
                <span style={{ color: '#525252' }}>
                  I'll guide you through questions and help you discover answers on your own.
                </span>
              </Stack>
            }
          />
        )}

        {messages.map(renderMessage)}

        {isTyping && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ backgroundColor: '#1890ff', marginRight: 8, borderRadius: '50%', padding: 8 }}>
              {/* Avatar placeholder */}
              <Education />
            </div>
            <Tile size="small" style={{ backgroundColor: '#f0f2f5', border: 'none' }}>
              <Stack>
                <Loading size="small" />
                <span style={{ color: '#525252' }}>Sigma is thinking...</span>
              </Stack>
            </Tile>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <Tile size="small" style={{ borderRadius: 0, borderTop: '1px solid #f0f0f0' }}>
        {connectionStatus.lastError && (
          <InlineNotification
            message={connectionStatus.lastError}
            type="error"
            closable
            style={{ marginBottom: 8 }}
            onClose={() => setConnectionStatus(prev => ({ ...prev, lastError: undefined }))}
          />
        )}

        <Space.Compact style={{ width: '100%' }}>
          <TextArea
            value={inputMessage}
            onChange={e => setInputMessage(e.target.value)}
            onPressEnter={e => {
              if (!e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder={
              connectionStatus.isConnected
                ? "Ask a question or share what you're thinking..."
                : 'Start a session to begin chatting'
            }
            disabled={!connectionStatus.isConnected || isTyping}
            autoSize={{ minRows: 1, maxRows: 4 }}
            style={{ borderRadius: '4px 0 0 4px' }}
          />
          <Button
            kind="primary"
            icon={<SendOutlined />}
            onClick={sendMessage}
            disabled={!connectionStatus.isConnected || !inputMessage.trim() || isTyping}
            style={{ height: 'auto', boxShadow: '0 4px 4px 0' }}
          >
            Send
          </Button>
        </Space.Compact>

        <div style={{ marginTop: 8 }}>
          <span style={{ color: '#525252' }}>
            <Security /> All conversations are monitored for educational safety and quality
          </span>
        </div>
      </Tile>
    </div>
  );
};

export default AiTutoringChat;
